import PropTypes from "prop-types"
import { useContext, useEffect } from "react"
import Canonical from "components/Canonical"
import { NextSeo } from "next-seo"
import { useLocale, useT } from "react-polyglot-hooks"
import { CarContext } from "scenes/CarScene/CarContext"
import Product from "components/structuredData/Product"
import { getPath } from "helpers/getPath"
import capitalize from "lodash/capitalize"
import { shouldHideFromSearch } from "helpers/seoHelpers"
import { useBusinessSeoDescription } from "components/GlobalSeoData/BusinessSeoContext"

export default function CarSeo({ hide }) {
  const t = useT()
  const locale = useLocale()

  const { car, slug } = useContext(CarContext)
  const { brand, model, version, minimumPrice, image, sellingStatus, fuel, transmission, environmentalLabel } = car
  const { setDescription, resetDescription } = useBusinessSeoDescription()

  const versionPagePrefix = `${sellingStatus === 'flexible' ? 'flexible_' : ''}version`
  const path = getPath(locale, versionPagePrefix, slug)
  const url = `https://swipcar.com/${locale}${path}`

  const pageDescription = t(`car_data.seo.${car.sellingStatus}.description`, {
    brand,
    model,
    version: version.replace('"', ""),
    price: minimumPrice,
    fuel: fuel ? `${capitalize(fuel)}.` : 'Todo incluido.',
    transmission: transmission ? `${capitalize(transmission)}.` : 'Seguro Todo Riesgo.',
    environmentalLabel: environmentalLabel ? `Etiqueta ${environmentalLabel}.` : 'Mantenimiento y Reparaciones.'
  })

  useEffect(() => {
    setDescription(pageDescription)
    return () => resetDescription()
  }, [pageDescription, setDescription, resetDescription])

  // Determinar si se debe ocultar de los motores de búsqueda
  const hideFromSearch = shouldHideFromSearch(hide)

  const pageTitle = car.humanizedDeliveryProvinces && car.humanizedDeliveryProvinces.length > 0
    ? t(`car_data.seo.${car.sellingStatus}.title_with_delivery_province`, {
      brand,
      model,
      version: version.replace('"', ""),
      province: car.humanizedDeliveryProvinces[0],
      price: minimumPrice
    })
    : t(`car_data.seo.${car.sellingStatus}.title`, {
      brand,
      model,
      version: version.replace('"', "")
    });

  return (
    <>
      <Canonical hreflang={false} page={versionPagePrefix} params={slug} />
      <NextSeo
        title={pageTitle}
        description={t(`car_data.seo.${car.sellingStatus}.description`, {
          brand,
          model,
          version: version.replace('"', ""),
          price: minimumPrice,
          fuel: fuel ? `${capitalize(fuel)}.` : 'Todo incluido.',
          transmission: transmission ? `${capitalize(transmission)}.` : 'Seguro Todo Riesgo.',
          environmentalLabel: environmentalLabel ? `Etiqueta ${environmentalLabel}.` : 'Mantenimiento y Reparaciones.'
        })}
        openGraph={{
          type: "website",
          locale: `${locale}_${locale.toUpperCase()}`,
          url,
          title: t(`car_data.seo.${car.sellingStatus}.og:title`, { brand, model }),
          description: t(`car_data.seo.${car.sellingStatus}.og:description`, { brand, model }),
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_CLOUDINARY}w_992,${image}`,
              width: 992,
              height: 661,
              alt: t(`car_data.seo.${car.sellingStatus}.og:title`, { brand, model })
            }
          ]
        }}
        noindex={hideFromSearch}
        nofollow={hideFromSearch}
      />
      <Product car={car} url={url} />
    </>
  )
}

CarSeo.defaultProps = {
  hide: false
}

CarSeo.propTypes = {
  hide: PropTypes.bool
}
